import { useState, useEffect } from 'react';
import { Search, MessageCircle, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { usePermissions } from '../../hooks/usePermissions';

// API base URL
const API_URL = `${process.env.REACT_APP_API_BASE_URL}/tickets`;

export default function SupportTickets() {
    const permissions = usePermissions('Support Tickets');
    const [tickets, setTickets] = useState([]);
    const [filteredTickets, setFilteredTickets] = useState([]);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [newMessage, setNewMessage] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');
    const [createTicketForm, setCreateTicketForm] = useState({
        subject: '',
        description: '',
        type: 'other'
    });
    const [showCreateForm, setShowCreateForm] = useState(false);

    const { loading, error, makeRequest, clearError, handleFetchResponse } = useAuthenticatedApi();

    // Current user info (would normally come from auth context)
    const currentUser = {
        userId: 'someUserId', // This would come from your auth system
        role: 'admin' // This would come from your auth system
    };

    // Fetch all tickets
    useEffect(() => {
        const fetchTickets = async () => {
            const queryParams = typeFilter !== 'all' ? `?type=${typeFilter}` : '';

            const response = await makeRequest(async (token) => {
                const fetchResponse = await fetch(`${API_URL}/all${queryParams}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    }
                });

                await handleFetchResponse(fetchResponse);
                return await fetchResponse.json();
            });

            if (response) {
                setTickets(response);
                setFilteredTickets(response);
            } else {
                setTickets([]);
                setFilteredTickets([]);
            }
        };

        fetchTickets();
    }, [typeFilter, makeRequest, handleFetchResponse]);

    // Filter tickets based on search term and status
    useEffect(() => {
        let filtered = [...tickets];

        // Filter by search term
        if (searchTerm) {
            filtered = filtered.filter(ticket =>
                ticket.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
                ticket.description.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Filter by status
        if (statusFilter !== 'all') {
            filtered = filtered.filter(ticket => ticket.status === statusFilter);
        }

        setFilteredTickets(filtered);
    }, [tickets, searchTerm, statusFilter]);

    // Handle ticket selection
    const handleSelectTicket = (ticket) => {
        setSelectedTicket(ticket);
    };

    // Handle message submission
    const handleSubmitMessage = async () => {
        if (!newMessage.trim() || !selectedTicket) return;

        const response = await makeRequest(async (token) => {
            const fetchResponse = await fetch(`${API_URL}/message?ticketId=${selectedTicket._id}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ text: newMessage })
            });

            await handleFetchResponse(fetchResponse);
            return await fetchResponse.json();
        });

        if (response) {
            // Update tickets list with new message
            const updatedTickets = tickets.map(ticket =>
                ticket._id === selectedTicket._id ? response : ticket
            );

            setTickets(updatedTickets);
            setSelectedTicket(response);
            setNewMessage('');
        }
    };

    // Handle status change
    const handleStatusChange = async (ticketId, newStatus) => {
        const response = await makeRequest(async (token) => {
            const fetchResponse = await fetch(`${API_URL}/ticket/${ticketId}/status`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ status: newStatus })
            });

            await handleFetchResponse(fetchResponse);
            return await fetchResponse.json();
        });

        if (response) {
            // Update tickets list with new status
            const updatedTickets = tickets.map(ticket =>
                ticket._id === ticketId ? response : ticket
            );

            setTickets(updatedTickets);

            if (selectedTicket && selectedTicket._id === ticketId) {
                setSelectedTicket(response);
            }
        }
    };

    // Handle create ticket form submission
    const handleCreateTicket = async (e) => {
        e.preventDefault();
        clearError();

        const response = await makeRequest(async (token) => {
            const fetchResponse = await fetch(`${API_URL}/create`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(createTicketForm)
            });

            await handleFetchResponse(fetchResponse);
            return await fetchResponse.json();
        });

        if (response) {
            // Add new ticket to list
            setTickets([response, ...tickets]);

            // Reset form and hide it
            setCreateTicketForm({ subject: '', description: '', type: 'other' });
            setShowCreateForm(false);

            // Select the new ticket
            setSelectedTicket(response);
        }
    };

    // Format date
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    // Get status icon
    const getStatusIcon = (status) => {
        switch (status) {
            case 'Open':
                return <AlertCircle className="text-blue-500" size={16} />;
            case 'In Progress':
                return <Clock className="text-yellow-500" size={16} />;
            case 'Resolved':
            case 'Closed':
                return <CheckCircle className="text-green-500" size={16} />;
            default:
                return <AlertCircle className="text-gray-500" size={16} />;
        }
    };

    // Create ticket form component
    const CreateTicketForm = () => (
        <div className="p-4 bg-white border border-gray-200 rounded-md shadow-sm">
            <h3 className="text-lg font-medium mb-4">Create New Support Ticket</h3>
            <form onSubmit={handleCreateTicket}>
                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Subject
                    </label>
                    <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={createTicketForm.subject}
                        onChange={(e) => setCreateTicketForm({ ...createTicketForm, subject: e.target.value })}
                        required
                    />
                </div>

                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                    </label>
                    <textarea
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        rows="4"
                        value={createTicketForm.description}
                        onChange={(e) => setCreateTicketForm({ ...createTicketForm, description: e.target.value })}
                        required
                    />
                </div>

                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Type
                    </label>
                    <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={createTicketForm.type}
                        onChange={(e) => setCreateTicketForm({ ...createTicketForm, type: e.target.value })}
                    >
                        <option value="order">Order</option>
                        <option value="payment">Payment</option>
                        <option value="account">Account</option>
                        <option value="technical">Technical</option>
                        <option value="other">Other</option>
                    </select>
                </div>

                <div className="flex justify-end space-x-2">
                    <button
                        type="button"
                        className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-md inline-flex items-center justify-center"
                        onClick={() => setShowCreateForm(false)}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md inline-flex items-center justify-center hover:bg-blue-700 transition-colors"
                    >
                        Submit Ticket
                    </button>
                </div>
            </form>
        </div>
    );

    return (
        <div className="flex flex-col md:flex-row h-screen bg-gray-100">
            {/* Ticket List Sidebar */}
            <div className="w-full md:w-1/3 bg-white border-r border-gray-200 flex flex-col">
                <div className="p-4 border-b border-gray-200">
                    <h2 className="text-xl font-semibold mb-4">Support Tickets</h2>

                    {/* Search */}
                    <div className="relative mb-4">
                        <input
                            type="text"
                            placeholder="Search tickets..."
                            className="w-full px-4 py-2 border border-gray-300 rounded-md pl-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <Search className="absolute top-3 left-3 text-gray-400" size={16} />
                    </div>

                    {/* Filters */}
                    <div className="flex space-x-2 mb-4">
                        {/* Status Filter */}
                        <select
                            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="all">All Status</option>
                            <option value="Open">Open</option>
                            <option value="In Progress">In Progress</option>
                            <option value="Resolved">Resolved</option>
                            <option value="Closed">Closed</option>
                        </select>

                        {/* Type Filter */}
                        <select
                            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            value={typeFilter}
                            onChange={(e) => setTypeFilter(e.target.value)}
                        >
                            <option value="all">All Types</option>
                            <option value="order">Order</option>
                            <option value="payment">Payment</option>
                            <option value="account">Account</option>
                            <option value="technical">Technical</option>
                            <option value="other">Other</option>
                        </select>
                    </div>

                    {/* Create New Ticket Button */}
                    {!showCreateForm && (
                        <button
                            className="w-full py-2 text-sm bg-blue-600 text-white rounded-md mb-4 inline-flex items-center justify-center hover:bg-blue-700 transition-colors"
                            onClick={() => setShowCreateForm(true)}
                        >
                            Create New Ticket
                        </button>
                    )}

                    {/* Create Ticket Form */}
                    {showCreateForm && <CreateTicketForm />}
                </div>

                {/* Ticket List */}
                <div className="flex-1 overflow-y-auto">
                    {loading ? (
                        <div className="p-4 text-center text-gray-500">Loading tickets...</div>
                    ) : error ? (
                        <div className="p-4 text-center text-red-500">{error}</div>
                    ) : filteredTickets.length === 0 ? (
                        <div className="p-4 text-center text-gray-500">No tickets found</div>
                    ) : (
                        filteredTickets.map(ticket => (
                            <div
                                key={ticket._id}
                                className={`p-4 border-b border-gray-200 cursor-pointer hover:bg-gray-50 ${selectedTicket && selectedTicket._id === ticket._id ? 'bg-blue-50' : ''
                                    }`}
                                onClick={() => handleSelectTicket(ticket)}
                            >
                                <div className="flex justify-between items-center mb-1">
                                    <h3 className="font-medium text-sm md:text-base">{ticket.subject}</h3>
                                    <div className="flex items-center">
                                        {getStatusIcon(ticket.status)}
                                        <span className="text-xs ml-1 text-gray-500">{ticket.status}</span>
                                    </div>
                                </div>
                                <p className="text-sm text-gray-600 truncate mb-2">{ticket.description}</p>
                                <div className="flex justify-between text-xs text-gray-500">
                                    <span className="bg-gray-100 px-2 py-1 rounded">
                                        {ticket.type}
                                    </span>
                                    <span>Updated: {formatDate(ticket.updatedAt)}</span>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Ticket Detail View */}
            <div className="w-full md:w-2/3 flex flex-col">
                {selectedTicket ? (
                    <>
                        {/* Ticket Header */}
                        <div className="p-4 bg-white border-b border-gray-200">
                            <div className="flex justify-between items-center">
                                <div>
                                    <h2 className="text-xl font-semibold">{selectedTicket.subject}</h2>
                                    <div className="flex space-x-2 text-sm text-gray-500">
                                        <span>ID: {selectedTicket._id}</span>
                                        <span>•</span>
                                        <span>Type: {selectedTicket.type}</span>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-2">
                                    {currentUser.role === 'admin' && (
                                        <select
                                            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            value={selectedTicket.status}
                                            onChange={(e) => handleStatusChange(selectedTicket._id, e.target.value)}
                                        >
                                            <option value="Open">Open</option>
                                            <option value="In Progress">In Progress</option>
                                            <option value="Resolved">Resolved</option>
                                            <option value="Closed">Closed</option>
                                        </select>
                                    )}

                                    <span
                                        className={`px-3 py-1 rounded-full text-xs ${selectedTicket.status === 'Open'
                                                ? 'bg-blue-100 text-blue-800'
                                                : selectedTicket.status === 'In Progress'
                                                    ? 'bg-yellow-100 text-yellow-800'
                                                    : 'bg-green-100 text-green-800'
                                            }`}
                                    >
                                        {selectedTicket.status}
                                    </span>
                                </div>
                            </div>
                            <p className="mt-2 text-gray-600">{selectedTicket.description}</p>
                            <div className="flex justify-between mt-4 text-sm text-gray-500">
                                <span>Created: {formatDate(selectedTicket.createdAt)}</span>
                                <span>Last Updated: {formatDate(selectedTicket.updatedAt)}</span>
                            </div>
                        </div>

                        {/* Messages */}
                        <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
                            {selectedTicket.messages && selectedTicket.messages.length > 0 ? (
                                selectedTicket.messages.map((message, index) => (
                                    <div
                                        key={index}
                                        className={`mb-4 flex ${message.sender === 'user' ? 'justify-start' : 'justify-end'
                                            }`}
                                    >
                                        <div
                                            className={`max-w-[75%] p-3 rounded-lg ${message.sender === 'user'
                                                    ? 'bg-white border border-gray-200'
                                                    : 'bg-blue-100'
                                                }`}
                                        >
                                            <div className="text-sm font-medium mb-1">
                                                {message.sender === 'user' ? 'Customer' : 'Support Agent'}
                                            </div>
                                            <p className="text-gray-700 text-sm">{message.text}</p>
                                            <div className="text-xs text-gray-500 mt-1">
                                                {formatDate(message.timestamp)}
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="text-center text-gray-500 mt-8">
                                    No messages yet. Start the conversation below.
                                </div>
                            )}
                        </div>

                        {/* Message Input */}
                        {selectedTicket.status !== 'Closed' && (
                            <div className="p-4 bg-white border-t border-gray-200">
                                <div className="flex space-x-2">
                                    <input
                                        type="text"
                                        className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Type your message..."
                                        value={newMessage}
                                        onChange={(e) => setNewMessage(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleSubmitMessage()}
                                    />
                                    <button
                                        className="px-4 py-2 bg-blue-600 text-white rounded-md flex items-center justify-center text-sm hover:bg-blue-700 transition-colors"
                                        onClick={handleSubmitMessage}
                                    >
                                        <MessageCircle size={16} className="mr-2" /> Send
                                    </button>
                                </div>

                                {/* Quick Actions for Admins */}
                                {currentUser.role === 'admin' && (
                                    <div className="mt-2 flex flex-wrap gap-2">
                                        <button
                                            className="inline-flex items-center px-3 py-1 rounded-md text-sm text-blue-600 hover:text-blue-800 border border-transparent"
                                            onClick={() => handleStatusChange(selectedTicket._id, 'In Progress')}
                                        >
                                            Mark In Progress
                                        </button>
                                        <button
                                            className="inline-flex items-center px-3 py-1 rounded-md text-sm text-green-600 hover:text-green-800 border border-transparent"
                                            onClick={() => handleStatusChange(selectedTicket._id, 'Resolved')}
                                        >
                                            Mark Resolved
                                        </button>
                                        <button
                                            className="inline-flex items-center px-3 py-1 rounded-md text-sm text-red-600 hover:text-red-800 border border-transparent"
                                            onClick={() => handleStatusChange(selectedTicket._id, 'Closed')}
                                        >
                                            Close Ticket
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Closed/Resolved Ticket Notice */}
                        {(selectedTicket.status === 'Closed' || selectedTicket.status === 'Resolved') && (
                            <div className="p-4 bg-gray-100 border-t border-gray-200 text-center">
                                <p className="text-gray-600">
                                    This ticket is {selectedTicket.status.toLowerCase()}.
                                </p>
                                {currentUser.role === 'admin' && (
                                    <button
                                        className="mt-2 inline-flex items-center px-3 py-1 rounded-md text-sm text-blue-600 hover:text-blue-800 border border-transparent"
                                        onClick={() => handleStatusChange(selectedTicket._id, 'Open')}
                                    >
                                        Reopen Ticket
                                    </button>
                                )}
                            </div>
                        )}
                    </>
                ) : (
                    <div className="flex-1 flex items-center justify-center bg-gray-50">
                        <div className="text-center text-gray-500">
                            <MessageCircle size={48} className="mx-auto mb-4 text-gray-400" />
                            <h3 className="text-xl font-medium mb-2">No Ticket Selected</h3>
                            <p>Select a ticket from the list to view details</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}